import React, { useEffect, useState } from "react";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { CssBaseline, Paper, Typography, withStyles, WithStyles } from "@material-ui/core";
import { isEmailValid } from "../../../utilities/generalUtils";
import { convertDateStringToNumber, today } from "../../../utilities/dateTimeUtils";
import { useDispatch, useSelector } from "react-redux";
import { findCategoryByName, findProductByName } from "../productOverview/ProductHelper";
import { useHistory, useParams } from "react-router-dom";
import * as A7ProductsRequestAction from "../../../stores/a7products/A7ProductsRequestAction";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { styles } from "./A7ProductDetails.styles";
import { DATE_FORMAT } from "../../../constants/TIME";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { isEuCountry } from "../../../utilities/countriesUtils";
import { A7PlatformAgreement } from "./A7PlatformAgreement";
import { selectVisibleCountries } from "../../../selectors/countries/CountriesSelector";

export interface IA7ProductDetailsProps extends WithStyles<typeof styles> {
}

const VALIDATION_VAT = "VAT must be set";
const VALIDATION_EMAIL = "Email is not valid";

const NAME_A7_PLATFORM = "A7 Analytics cloud platform";
const A7ProductDetailsPure: React.FunctionComponent<IA7ProductDetailsProps> = ({classes}) => {

    const {enqueueSnackbar} = useSnackbar();
    const history = useHistory();
    const dispatch = useDispatch();

    const countries = useSelector(state => selectVisibleCountries(state));

    const [errors, setErrors] = useState({} as any);

    const a7productsInfo = useSelector((state) => state.a7products);

    const {productIdentifier} = useParams();
    const thisProduct = findProductByName(a7productsInfo?.products, productIdentifier);
    const thisCategory = findCategoryByName(a7productsInfo?.categories, thisProduct?.category);

    const availableProducts = a7productsInfo?.products?.map(p => ({name: p.name, shortcut: p.id}));
    const userDetails = useSelector(state => state.userDetails?.currentlyLoggedUser);

    const [details, setDetails] = React.useState({
        title: "",
        firstName: "",
        lastName: "",
        companyName: "",
        companyPhone: "",
        companyEmail: "",
        street: "",
        postalCode: "",
        city: "",
        country: "",
        vat: "",
        product: "",
        desiredEffectiveDate: today().format(DATE_FORMAT),
        message: "",
        termsConditions: "",
        confirmLegalEntity: "",
        //honeypot
        website: "",
    });

    const handleChange = event => {
        const name = event.target.name;
        const value = event.target.value;

        setDetails({
            ...details,
            [name]: value
        });

        let newErrors: any = {...errors};
        if (name === "companyEmail") {
            if (value) {
                newErrors[name] = isEmailValid(value) ? "" : VALIDATION_EMAIL;
            }
        }
        if (name === "country") {
            newErrors["vat"] = isEuCountry(countries, value) && details.vat === "" ? VALIDATION_VAT : "";
        }
        if (name === "vat") {
            newErrors["vat"] = isEuCountry(countries, details.country) && value === "" ? VALIDATION_VAT : "";
        }

        setErrors({
            ...newErrors
        });
    };

    const sendContactRequest = event => {
        event.preventDefault();
        const {vat, desiredEffectiveDate, website, ...filteredDetails} = details;
        const request = {
            ...filteredDetails,
            tin: details.vat,
            effectiveDate: convertDateStringToNumber(details.desiredEffectiveDate)
        };
        if (details.website === "") {
            dispatch(A7ProductsRequestAction.makeRequest(request)).then(() => {
                enqueueSnackbar("Message has been sent", {
                    variant: ToastStatusEnum.Info
                });
                history.push("/a7product/success");
            });
        }
    };

    useEffect(() => {
        if (details.product === "" && Array.isArray(availableProducts) && availableProducts.length > 0) {
            setDetails(details => ({
                ...details,
                product: availableProducts[0].shortcut
            }));
        }
    }, [details.product, availableProducts]);

    useEffect(() => {
        if (userDetails) {
            setDetails(details => ({
                ...details,
                firstName: userDetails.firstName,
                lastName: userDetails.lastName,
                companyName: userDetails.company,
                companyEmail: userDetails.email,
                companyPhone: userDetails.phone,
                vat: userDetails.tin,
                street: userDetails.billingAddress.addressLine1,
                postalCode: userDetails.billingAddress.postcode,
                city: userDetails.billingAddress.city,
                country: userDetails.billingAddress.country,
            }))
        }
    }, [userDetails]);

    const categoryDescription = thisCategory?.description?.replace(NAME_A7_PLATFORM, "<a href='https://www.mds.deutsche-boerse.com/mds-de/analytics/a7-analytics-platform' target='_blank' rel='noreferrer'>A7 Analytics cloud platform</a>");
    return (
        <>
            <CenteredContainer>
                <Breadcrumbs
                    items={[
                        {title: "Product overview", href: "/products"},
                        {title: thisProduct?.name}
                    ]}
                />
                <CssBaseline />
                <div className={classes.content}>
                    <Typography variant="h1">{thisCategory?.name}</Typography>
                    <Typography variant="body1" className={classes.description}>
                        <span dangerouslySetInnerHTML={{__html: categoryDescription}} />
                    </Typography>
                    <Paper className={classes.background}>
                        <A7PlatformAgreement handleChange={handleChange}
                                             details={details}
                                             product={thisProduct}
                                             errors={errors}
                                             handleSubmit={sendContactRequest}
                                             hasNonEditableFields={userDetails !== undefined}
                        />
                    </Paper>
                </div>
            </CenteredContainer>

        </>
    );
};

export const A7ProductDetails = withStyles(styles)(A7ProductDetailsPure);
